/**
 * Unified Modal Management System
 * 
 * Provides a centralized way to manage modals across the application
 * with consistent loading states and error handling.
 */

import { createContext, useContext, useState, ReactNode } from 'react';

interface ModalState {
  [key: string]: boolean;
}

interface ModalContextType {
  modals: ModalState;
  openModal: (modalId: string) => void;
  closeModal: (modalId: string) => void;
  toggleModal: (modalId: string) => void;
  isOpen: (modalId: string) => boolean;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider = ({ children }: ModalProviderProps) => {
  const [modals, setModals] = useState<ModalState>({});

  const openModal = (modalId: string) => {
    setModals(prev => ({ ...prev, [modalId]: true }));
  };

  const closeModal = (modalId: string) => {
    setModals(prev => ({ ...prev, [modalId]: false }));
  };

  const toggleModal = (modalId: string) => {
    setModals(prev => ({ ...prev, [modalId]: !prev[modalId] }));
  };

  const isOpen = (modalId: string) => {
    return !!modals[modalId];
  };

  return (
    <ModalContext.Provider value={{ modals, openModal, closeModal, toggleModal, isOpen }}>
      {children}
    </ModalContext.Provider>
  );
};